import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import LangToggle from "@/components/LangToggle";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, PlayCircle, FileText, Users, BookOpen, Download, Mail, Phone, Globe, Loader2, ChevronRight,
  Coins, Flame, Gem, Mountain, Watch, Target, Crown, Network,
  Handshake, Landmark, ShieldAlert, LineChart, Calculator, Briefcase, Atom, Wheat, Brain, Dumbbell,
} from "lucide-react";
import GoldPage from "@/pages/domains/GoldPage";
import OilPage from "@/pages/domains/OilPage";
import GemsPage from "@/pages/domains/GemsPage";
import CementPage from "@/pages/domains/CementPage";
import WatchesPage from "@/pages/domains/WatchesPage";
import MetalsPage from "@/pages/domains/MetalsPage";
import SoftCommoditiesPage from "@/pages/domains/SoftCommoditiesPage";
import DealIntelligencePage from "@/pages/modules/DealIntelligencePage";
import PowerPositioningPage from "@/pages/modules/PowerPositioningPage";
import NetworkAccessPage from "@/pages/modules/NetworkAccessPage";
import NegotiationPage from "@/pages/modules/NegotiationPage";
import BankingPage from "@/pages/modules/BankingPage";
import SecurityPage from "@/pages/modules/SecurityPage";
import PricingPage from "@/pages/modules/PricingPage";
import TaxationPage from "@/pages/modules/TaxationPage";
import ToolkitPage from "@/pages/modules/ToolkitPage";
import MindsetPage from "@/pages/modules/MindsetPage";

const TABS = [
  { id: "modules", icon: BookOpen, group: "Formation" },
  { id: "negotiation", icon: Handshake, group: "Formation" },
  { id: "banking", icon: Landmark, group: "Formation" },
  { id: "security", icon: ShieldAlert, group: "Formation" },
  { id: "pricing", icon: LineChart, group: "Formation" },
  { id: "taxation", icon: Calculator, group: "Formation" },
  { id: "toolkit", icon: Briefcase, group: "Formation" },
  { id: "gold", icon: Coins, group: "Secteurs" },
  { id: "oil", icon: Flame, group: "Secteurs" },
  { id: "gems", icon: Gem, group: "Secteurs" },
  { id: "watches", icon: Watch, group: "Secteurs" },
  { id: "cement", icon: Mountain, group: "Secteurs" },
  { id: "metals", icon: Atom, group: "Secteurs" },
  { id: "soft", icon: Wheat, group: "Secteurs" },
  { id: "mindset", icon: Brain, group: "Stratégie" },
  { id: "deal-intel", icon: Target, group: "Stratégie" },
  { id: "power", icon: Crown, group: "Stratégie" },
  { id: "network", icon: Network, group: "Stratégie" },
  { id: "documents", icon: FileText, group: "Ressources" },
  { id: "exercices", icon: Dumbbell, group: "Ressources" },
  { id: "suppliers", icon: Users, group: "Ressources" },
];

const GROUP_KEYS = {
  Formation: "dash.group.training",
  Secteurs: "dash.group.sectors",
  Stratégie: "dash.group.strategy",
  Ressources: "dash.group.resources",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [tab, setTab] = useState("modules");
  const [modules, setModules] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = t("dash.title");
    (async () => {
      try {
        const [m, d, s] = await Promise.all([
          api.get("/content/modules"),
          api.get("/content/documents"),
          api.get("/content/suppliers"),
        ]);
        setModules(m.data);
        setDocuments(d.data);
        setSuppliers(s.data);
        if (m.data?.length) setActiveModule(m.data[0]);
      } catch {
        toast.error(t("dash.error_load"));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const onLogout = async () => {
    await logout();
    toast.success(t("dash.logout_toast"));
    nav("/");
  };

  const downloadDocument = (doc) => {
    const token = localStorage.getItem("aurum_token");
    const url = `${API}/content/documents/${doc.id}/download?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#A1A1AA]">
        <Loader2 className="h-5 w-5 animate-spin mr-3" />
        <span className="text-xs uppercase tracking-[0.3em]">{t("common.loading_dashboard")}</span>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen flex flex-col md:flex-row bg-[#0A0A0A]">
      {/* Sidebar */}
      <aside className="md:w-72 md:min-h-screen md:border-r md:border-white/10 bg-[#0c0c0c] md:sticky md:top-0">
        <div className="p-6 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 flex items-center justify-center border border-[#D4AF37]/70 text-[#D4AF37] font-serif italic">A</div>
            <div>
              <div className="font-serif text-base leading-none">AURUM</div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA] mt-1">Academy</div>
            </div>
          </div>
          <LangToggle testid="dash-lang-toggle" />
        </div>

        <div className="p-6 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA]">{t("dash.learner")}</div>
          <div className="font-serif text-lg mt-2" data-testid="learner-label">{user?.code_label}</div>
          <div className="text-xs text-[#52525B] mt-1">{t("dash.session_active")}</div>
        </div>

        <nav className="p-4">
          {["Formation", "Secteurs", "Stratégie", "Ressources"].map((group) => (
            <div key={group} className="mb-5">
              <div className="px-4 mb-2 text-[9px] uppercase tracking-[0.32em] text-[#52525B]">{t(GROUP_KEYS[group])}</div>
              {TABS.filter((t2) => t2.group === group).map((tabItem) => {
                const Icon = tabItem.icon;
                const active = tab === tabItem.id;
                return (
                  <button
                    key={tabItem.id}
                    data-testid={`nav-${tabItem.id}`}
                    onClick={() => setTab(tabItem.id)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-sm mb-1 border transition-colors ${
                      active
                        ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#F5F5DC]"
                        : "border-transparent text-[#A1A1AA] hover:text-white hover:border-white/10"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="h-4 w-4" />
                      {t(`tab.${tabItem.id}`)}
                    </span>
                    {active && <ChevronRight className="h-4 w-4 text-[#D4AF37]" />}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="p-4 md:absolute md:bottom-0 md:w-72 border-t border-white/10 space-y-2">
          {user?.code_label?.startsWith("Admin") && (
            <a
              href="/admin"
              data-testid="nav-admin-link"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/10 transition-colors"
            >
              {t("dash.admin_panel")}
            </a>
          )}
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/15 text-sm text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
          >
            <LogOut className="h-4 w-4" /> {t("dash.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 px-6 md:px-12 py-10 md:py-14 max-w-6xl">
        {tab === "modules" && (
          <ModulesView modules={modules} active={activeModule} setActive={setActiveModule} />
        )}
        {tab === "gold" && <GoldPage onDownload={downloadDocument} documents={documents} />}
        {tab === "oil" && <OilPage onDownload={downloadDocument} documents={documents} />}
        {tab === "gems" && <GemsPage onDownload={downloadDocument} documents={documents} />}
        {tab === "watches" && <WatchesPage onDownload={downloadDocument} documents={documents} />}
        {tab === "cement" && <CementPage onDownload={downloadDocument} documents={documents} />}
        {tab === "deal-intel" && <DealIntelligencePage />}
        {tab === "power" && <PowerPositioningPage />}
        {tab === "network" && <NetworkAccessPage />}
        {tab === "negotiation" && <NegotiationPage />}
        {tab === "banking" && <BankingPage />}
        {tab === "security" && <SecurityPage />}
        {tab === "pricing" && <PricingPage />}
        {tab === "taxation" && <TaxationPage />}
        {tab === "toolkit" && <ToolkitPage />}
        {tab === "metals" && <MetalsPage />}
        {tab === "soft" && <SoftCommoditiesPage />}
        {tab === "mindset" && <MindsetPage />}
        {tab === "documents" && <DocumentsView documents={documents.filter((d) => (d.category || "model") !== "exercices")} onDownload={downloadDocument} />}
        {tab === "exercices" && <ExercicesView documents={documents.filter((d) => d.category === "exercices")} onDownload={downloadDocument} />}
        {tab === "suppliers" && <SuppliersView groups={suppliers} />}
      </main>
    </div>
  );
}

// ---------- Modules ----------
const ModulesView = ({ modules, active, setActive }) => {
  const { t } = useT();
  return (
    <div data-testid="view-modules">
      <div className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("modulesview.eyebrow")}</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light mt-3 leading-tight">
          {t("modulesview.title")}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 space-y-2">
          {modules.map((m, i) => {
            const isActive = active?.id === m.id;
            return (
              <button
                key={m.id}
                data-testid={`module-item-${m.id}`}
                onClick={() => setActive(m)}
                className={`w-full text-left p-5 border transition-colors ${
                  isActive ? "bg-[#141414] border-[#D4AF37]/40" : "bg-[#0c0c0c] border-white/10 hover:border-white/25"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{t(`module.${m.id}.category`, m.category)}</span>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA]">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="font-serif text-lg mt-3 leading-snug">{t(`module.${m.id}.title`, m.title)}</div>
                <div className="text-xs text-[#A1A1AA] mt-2 flex items-center gap-2">
                  <BookOpen className="h-3.5 w-3.5" /> {t(`module.${m.id}.duration`, m.duration)}
                </div>
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-8">
          {active && (
            <article className="border border-white/10 bg-[#0c0c0c]">
              {active.video_url ? (
                <div className="aspect-video bg-black border-b border-white/10">
                  <iframe
                    key={active.id}
                    src={active.video_url}
                    title={active.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : null}
              <div className="p-8 md:p-10">
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{t(`module.${active.id}.category`, active.category)}</span>
                <h2 className="font-serif text-3xl md:text-4xl font-light mt-3 leading-tight">{t(`module.${active.id}.title`, active.title)}</h2>
                <p className="mt-4 text-[#A1A1AA]">{t(`module.${active.id}.description`, active.description)}</p>
                {active.reading && (
                  <div className="mt-8 pt-8 border-t border-white/10">
                    <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{t("modulesview.notes")}</p>
                    <p className="mt-4 text-[#E4E4E7] leading-relaxed">{t(`module.${active.id}.reading`, active.reading)}</p>
                  </div>
                )}
              </div>
            </article>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Documents ----------
const DocumentsView = ({ documents, onDownload }) => {
  const { t } = useT();
  return (
    <div data-testid="view-documents">
      <div className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("docview.eyebrow")}</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light mt-3 leading-tight">
          {t("docview.title")}
        </h1>
        <p className="mt-4 text-[#A1A1AA] max-w-2xl">
          {t("docview.subtitle")}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {documents.map((d, i) => (
          <div key={d.id} data-testid={`document-card-${d.id}`} className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8 hover:border-white/25 transition-colors group">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-serif text-xl">{d.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA] mt-1">{t("docview.tag")}</div>
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA]">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed">{d.description}</p>
            <button
              onClick={() => onDownload(d)}
              data-testid={`download-${d.id}`}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> {t("common.download")}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Exercices pratiques ----------
const ExercicesView = ({ documents, onDownload }) => {
  const { t } = useT();
  return (
    <div data-testid="view-exercices">
      <div className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("exview.eyebrow")}</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light mt-3 leading-tight">
          {t("exview.title1")} <em className="italic text-[#D4AF37]">{t("exview.title2")}</em>.
        </h1>
        <p className="mt-4 text-[#A1A1AA] max-w-2xl leading-relaxed">
          {t("exview.intro")}
        </p>
      </div>
      {documents.length === 0 ? (
        <div className="border border-white/10 bg-[#0c0c0c] p-10 text-center text-[#A1A1AA]">
          {t("exview.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {documents.map((d, i) => (
            <div key={d.id} data-testid={`exercice-card-${d.id}`} className="border border-[#D4AF37]/30 bg-[#0c0c0c] p-7 md:p-8 hover:border-[#D4AF37]/60 transition-colors group">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 border border-[#D4AF37]/50 bg-[#D4AF37]/5 flex items-center justify-center text-[#D4AF37]">
                    <Dumbbell className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{t("exview.label")} {String(i + 1).padStart(2, "0")}</div>
                    <div className="font-serif text-xl mt-1">{d.name}</div>
                  </div>
                </div>
              </div>
              <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed">{d.description}</p>
              <button
                onClick={() => onDownload(d)}
                data-testid={`download-${d.id}`}
                className="mt-7 inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.22em] hover:bg-[#F5F5DC] transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> {t("exview.download")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Suppliers ----------
const SuppliersView = ({ groups }) => {
  const { t, lang } = useT();
  return (
    <div data-testid="view-suppliers">
      <div className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("supview.eyebrow")}</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light mt-3 leading-tight">
          {t("supview.title1")} <em className="italic text-[#D4AF37]">{t("supview.title2")}</em>{t("supview.title3") ? ` ${t("supview.title3")}` : ""}.
        </h1>
        <p className="mt-4 text-[#A1A1AA] max-w-2xl">
          {t("supview.intro")}
        </p>
      </div>

      <div className="space-y-14">
        {groups.map((g) => (
          <section key={g.category} data-testid={`supplier-group-${g.category}`}>
            <div className="flex items-baseline justify-between border-b border-white/10 pb-4 mb-6">
              <h2 className="font-serif text-2xl md:text-3xl font-light">{t(`supcat.${g.category}`, g.category)}</h2>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">{g.suppliers.length} {t("supview.count_label")}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {g.suppliers.map((s) => (
                <div key={s.id} className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-white/25 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{s.country}</div>
                    <Globe className="h-3.5 w-3.5 text-[#A1A1AA]" />
                  </div>
                  <div className="font-serif text-lg mt-3 leading-snug">{s.name}</div>
                  <div className="text-xs text-[#A1A1AA] mt-2">{lang === "en" && s.specialty_en ? s.specialty_en : s.specialty}</div>
                  <div className="mt-5 pt-5 border-t border-white/10 space-y-2 text-xs">
                    <SupplierWebsite raw={s.email} />
                    <a href={`tel:${s.phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-[#E4E4E7] hover:text-[#D4AF37] transition-colors">
                      <Phone className="h-3.5 w-3.5" /> {s.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

const SupplierWebsite = ({ raw }) => {
  const { t } = useT();
  if (!raw || raw === "—" || raw.trim() === "") {
    return (
      <div className="flex items-center gap-2 text-[#52525B]">
        <Globe className="h-3.5 w-3.5" /> <span className="italic">{t("supview.no_website")}</span>
      </div>
    );
  }
  const isEmail = /\S+@\S+\.\S+/.test(raw) && !raw.startsWith("http") && !raw.startsWith("www");
  if (isEmail) {
    return (
      <a href={`mailto:${raw}`} className="flex items-center gap-2 text-[#E4E4E7] hover:text-[#D4AF37] transition-colors break-all">
        <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{raw}</span>
      </a>
    );
  }
  // Otherwise treat as a website URL
  const href = raw.startsWith("http") ? raw : `https://${raw.replace(/^\/+/, "")}`;
  const display = raw.replace(/^https?:\/\//, "").replace(/^www\./, "");
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 text-[#E4E4E7] hover:text-[#D4AF37] transition-colors break-all"
    >
      <Globe className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{display}</span>
    </a>
  );
};

